import { ChatMessage, ChatReasoning } from "@/schema/chat";
import { AgentSettings } from "@/constants/agent-settings";
import { AgentIgnoreTools } from "@/constants/agent-tools";
import { StudioApiSettings, StudioImage, StudioSettings } from "@/types/agent";

export function checkAgentStreamSentinel(chunk: string) {
  return ["done", "[DONE]", "chat_interaction_done"].some((status) => status.trim() === chunk.trim());
}

export function sanitizeMessageContent(content: string) {
  return content.replace("&#8203;", "");
}

export function isAgentInterrupted(chat: ChatMessage[] | null | undefined) {
  if (!chat || !chat.length) return false;

  const message = chat[chat.length - 1];
  if (message.type !== "ai" || !message.reasoning) return false;

  const reasoning = message.reasoning;
  if (!reasoning || !reasoning.length) return false;

  const interrupted = reasoning[reasoning.length - 1].type === "response.interrupt";
  return interrupted;
}

export function extractAgentSettings(chat: ChatMessage[] | null | undefined) {
  const settings: Record<string, string> = { platform: "", aspectRatio: "", duration: "", mode: "" };
  if (!chat || !chat.length) return settings;

  const lastmessage = chat.findLast((message) => message.type === "user");
  if (!lastmessage) return settings;

  const message = lastmessage.message as string;
  const regex = /Settings:\s*\n((?:- .*\n?)*)/i;

  const match = message.match(regex);
  if (!match) return settings;

  const parsed = match[1]
    .split("\n")
    .filter((line) => line.trim().startsWith("- "))
    .map((line) => {
      const content = line.replace(/^-\s*/, "");
      const colon = content.indexOf(":");
      if (colon === -1) return null;
      const key = content.substring(0, colon).trim();
      const value = content.substring(colon + 1).trim();
      return { key, value };
    })
    .filter((setting) => !!setting);

  for (const setting of parsed) {
    const key = AgentSettings.Values[setting.key];
    if (key) settings[key] = setting.value;
  }

  return settings;
}

export function isAgentToolIgnored(tool: ChatReasoning) {
  return AgentIgnoreTools.includes(tool.name) || tool.name.startsWith("transfer_to");
}

export function transformAgentSettings(_: string, value: string) {
  return value;
}

export function ignoreAgentSettings(key: string, value: string) {
  if (AgentSettings.Configurations.includes(key)) {
    return true;
  }
  switch (key) {
    case "stylePreset":
      return value === AgentSettings.MediaDirector.Defaults.StylePreset;
    default:
      return false;
  }
}

export function transformAgentStudioSettings(settings: StudioSettings, prompt: string, first: StudioImage | null, last: StudioImage | null, references: StudioImage[]): Record<string, any> {
  return {
    prompt: prompt,
    model: settings.model,
    aspect_ratio: settings.aspectRatio,
    first_frame: first ? { url: first.url, mime_type: first.mime_type } : null,
    last_frame: last ? { url: last.url, mime_type: last.mime_type } : null,
    reference_images: references.length ? references.map((reference) => ({ url: reference.url, mime_type: reference.mime_type })) : null,
    negative_prompt: settings.negativePrompt ? settings.negativePrompt : null,
  } as StudioApiSettings;
}

export function sanitizeChoice(choice: string) {
  return choice
    .replace(/^[1-9]\./, "")
    .replace(/ [A-Za-z0-9-]+-[A-Za-z0-9]{6}/g, "")
    .trim();
}

export function isAgentToolInsufficientCredits(tool: ChatReasoning) {
  return tool.content && tool.content.status && typeof tool.content.status === "string" && tool.content.status === "INSUFFICIENT_CREDITS";
}
